import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';

export type DmsPinnedTemplate = {
    id: string;
    name: string;
};

@Injectable({ providedIn: 'root' })
export class DmsPinnedTemplatesService {
    private readonly _changes$ = new BehaviorSubject<void>(undefined);

    /** Emits when pinned templates change (sidebar should refresh). */
    readonly changes$ = this._changes$.asObservable();

    list(): DmsPinnedTemplate[] {
        return this._read();
    }

    isPinned(templateId: string): boolean {
        return this._read().some((row) => row.id === templateId);
    }

    pin(template: DmsPinnedTemplate): void {
        const rows = this._read().filter((row) => row.id !== template.id);
        rows.unshift({ id: template.id, name: template.name });
        this._write(rows);
    }

    unpin(templateId: string): void {
        this._write(this._read().filter((row) => row.id !== templateId));
    }

    toggle(template: DmsPinnedTemplate): void {
        if (this.isPinned(template.id)) {
            this.unpin(template.id);
        } else {
            this.pin(template);
        }
    }

    private _storageKey(): string {
        const email = (environment.user?.profile?.email || 'anonymous').toLowerCase();
        const org =
            environment.selection?.organization?.code ||
            environment.selection?.organization?.slug ||
            'org';
        return `dms-pinned-templates:${org}:${email}`;
    }

    private _read(): DmsPinnedTemplate[] {
        try {
            const raw = localStorage.getItem(this._storageKey());
            if (!raw) {
                return [];
            }
            const parsed = JSON.parse(raw) as DmsPinnedTemplate[];
            return Array.isArray(parsed) ? parsed.filter((row) => row?.id && row?.name) : [];
        } catch {
            return [];
        }
    }

    private _write(rows: DmsPinnedTemplate[]): void {
        localStorage.setItem(this._storageKey(), JSON.stringify(rows));
        this._changes$.next(undefined);
    }
}
